import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useToast } from "@/hooks/useToast";
import { LoginArea } from "@/components/auth/LoginArea";
import { BchLogo } from "./BchLogo";
import { AnimatedNumber } from "./AnimatedNumber";
import { Confetti } from "./Confetti";
import { track } from "@/lib/track";
import { formatFee, formatUsd } from "../engine";
import { buildInsights } from "../insights";
import { buildShareText, shareToX } from "../share";
import type { GameMode, RunResult } from "../types";

interface ResultsScreenProps {
  mode: GameMode;
  primary: RunResult;
  bch: RunResult;
  btc: RunResult;
  onRestart: () => void;
}

const SHARE_URL = "https://surviveonbch.com";

export function ResultsScreen({ mode, primary, bch, btc, onRestart }: ResultsScreenProps) {
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const shareText = buildShareText(mode, primary, bch, btc);

  function handleNostrShare() {
    publish(
      {
        kind: 1,
        content: `${shareText}\n\nPlay: ${SHARE_URL}`,
        tags: [
          ["t", "BitcoinCash"],
          ["t", "BCH"],
        ],
      },
      {
        onSuccess: () => {
          track("share_nostr");
          toast({ title: "Shared to Nostr!", description: "Your score is now on Nostr. 🟢" });
        },
        onError: () => {
          toast({
            title: "Couldn't publish",
            description: "Something went wrong sharing to Nostr.",
            variant: "destructive",
          });
        },
      },
    );
  }

  const feeDiff = Math.max(0, btc.totalFeesUsd - bch.totalFeesUsd);
  const insights = buildInsights(bch, btc);

  return (
    <div className="flex flex-col gap-5 animate-screen-enter">
      <Confetti fire={primary.survived} />

      {/* Hero result */}
      <Card className={primary.survived ? "border-primary/60" : "border-destructive/60"}>
        <CardContent className="p-6 text-center flex flex-col items-center gap-3">
          <div className="text-5xl" aria-hidden>{primary.survived ? "🎉" : "💀"}</div>
          <h1 className="text-2xl font-black text-foreground">
            {primary.survived ? "You Survived!" : "You Didn't Make It"}
          </h1>
          <p className="text-sm text-muted-foreground">
            7 days in <span className="font-semibold">{mode} Mode</span>
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-black text-primary">
              <AnimatedNumber
                value={primary.score}
                format={(n) => `${Math.round(n)}`}
                duration={900}
                flash={false}
              />
            </span>
            <span className="text-sm text-muted-foreground">/ 100 score</span>
          </div>
        </CardContent>
      </Card>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Total earned" value={`+${formatUsd(primary.totalEarnedUsd)}`} accent />
        <Stat label="Total spent" value={formatUsd(primary.totalSpentUsd)} />
        <Stat
          label="Simulated fees"
          value={formatFee(primary.totalFeesUsd)}
          accent
          sub={primary.spikeFeesUsd > 0 ? `${formatUsd(primary.spikeFeesUsd)} from spikes` : undefined}
        />
        <Stat label="Discounts saved" value={formatUsd(primary.totalSavedUsd)} />
        <Stat label="Final mood" value={`${Math.round(primary.wellbeing)}%`} />
        <Stat label="Fiat left" value={formatUsd(primary.remainingFiatUsd)} />
        <Stat label={`${mode} left`} value={formatUsd(primary.remainingCryptoUsd)} accent />
      </div>

      {/* Why the difference? — explanatory takeaways */}
      <Card className="border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Why the difference?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {insights.map((ins, i) => (
            <div key={i} className="flex gap-3">
              <div className="text-xl shrink-0" aria-hidden>{ins.icon}</div>
              <div className="min-w-0">
                <p
                  className={`text-sm font-semibold ${
                    ins.favors === "BCH"
                      ? "text-primary"
                      : ins.favors === "BTC"
                        ? "text-foreground"
                        : "text-foreground"
                  }`}
                >
                  {ins.title}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">{ins.detail}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* BCH vs BTC comparison */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">BCH vs BTC — same decisions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ComparisonRow label="Final score" bch={`${bch.score}`} btc={`${btc.score}`} bchWins={bch.score >= btc.score} />
          {(bch.totalEarnedUsd > 0 || btc.totalEarnedUsd > 0) && (
            <ComparisonRow
              label="Net income kept"
              bch={formatUsd(bch.totalEarnedUsd)}
              btc={formatUsd(btc.totalEarnedUsd)}
              bchWins={bch.totalEarnedUsd >= btc.totalEarnedUsd}
            />
          )}
          <ComparisonRow label="Fees paid" bch={formatFee(bch.totalFeesUsd)} btc={formatFee(btc.totalFeesUsd)} bchWins={bch.totalFeesUsd <= btc.totalFeesUsd} />
          {(bch.spikeFeesUsd > 0 || btc.spikeFeesUsd > 0) && (
            <ComparisonRow
              label="…of that, from spikes"
              bch={formatFee(bch.spikeFeesUsd)}
              btc={formatFee(btc.spikeFeesUsd)}
              bchWins={bch.spikeFeesUsd <= btc.spikeFeesUsd}
            />
          )}
          <ComparisonRow
            label="Total money left"
            bch={formatUsd(bch.remainingFiatUsd + bch.remainingCryptoUsd)}
            btc={formatUsd(btc.remainingFiatUsd + btc.remainingCryptoUsd)}
            bchWins={bch.remainingFiatUsd + bch.remainingCryptoUsd >= btc.remainingFiatUsd + btc.remainingCryptoUsd}
          />
          {feeDiff > 0.5 && (
            <p className="text-xs text-center bg-primary/10 text-primary rounded-lg px-3 py-2 font-medium">
              💡 Paying in BCH saved ~{formatUsd(feeDiff)} in fees vs BTC over the week.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Decisions log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your decisions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {primary.decisions.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-0 last:pb-0">
              <div className="min-w-0">
                <p className="font-medium text-foreground truncate">Day {d.day} · {d.scenarioTitle}</p>
                <p className="text-xs text-muted-foreground">
                  {d.flow === "earn"
                    ? d.method === "crypto"
                      ? `Received in ${mode}`
                      : "Received in fiat"
                    : d.method === "crypto"
                      ? `Paid with ${mode}`
                      : "Paid with fiat"}
                  {d.flow === "spend" && d.savedUsd > 0 ? ` · saved ${formatUsd(d.savedUsd)}` : ""}
                  {d.flow === "earn" && d.savedUsd > 0 ? ` · lost ${formatUsd(d.savedUsd)} to cut` : ""}
                </p>
              </div>
              <div className="text-right shrink-0 ml-2">
                {d.flow === "earn" ? (
                  <p className="font-semibold text-primary">+{formatUsd(d.earnedUsd)}</p>
                ) : (
                  <p className="font-semibold text-foreground">{formatUsd(d.costUsd)}</p>
                )}
                {d.feeUsd > 0 && (
                  <p className={`text-[11px] ${d.feeSpiked ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                    {d.flow === "earn" ? "−" : "+"}{formatFee(d.feeUsd)} fee{d.feeSpiked ? " ⚠️" : ""}
                  </p>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Sharing */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Share your result</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {user ? (
            <Button className="w-full h-12 font-bold" onClick={handleNostrShare} disabled={isPending}>
              {isPending ? "Publishing…" : "Share to Nostr 🟣"}
            </Button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Log in with Nostr to share your score.</p>
              <LoginArea className="w-full" />
            </div>
          )}
          <Button
            variant="secondary"
            className="w-full h-12 font-bold"
            onClick={() => {
              track("share_x");
              shareToX(shareText, SHARE_URL);
            }}
          >
            Share to X 𝕏
          </Button>
        </CardContent>
      </Card>

      {/* BCHNostr CTA */}
      <Card className="bg-primary/5 border-primary/30">
        <CardContent className="p-5 flex flex-col items-center text-center gap-3">
          <BchLogo className="w-12 h-12 text-2xl" />
          <p className="text-sm font-semibold text-foreground">Want more P2P digital cash?</p>
          <p className="text-xs text-muted-foreground">
            Join the BCH community on Nostr and connect with fellow believers in peer-to-peer money.
          </p>
          <Button asChild variant="outline" className="border-primary text-primary">
            <a href="https://bchnostr.com" target="_blank" rel="noopener noreferrer">
              Join BCHNostr →
            </a>
          </Button>
        </CardContent>
      </Card>

      <Button variant="ghost" className="w-full" onClick={onRestart}>
        ↻ Play Again
      </Button>

      <p className="text-[11px] text-center text-muted-foreground pb-2">
        All values in this game are simulated. No real money or crypto was used.
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  sub,
}: {
  label: string;
  value: string;
  accent?: boolean;
  sub?: string;
}) {
  return (
    <div className="rounded-xl bg-card border border-border px-4 py-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
      {sub && <p className="text-[10px] text-destructive font-medium mt-0.5">{sub}</p>}
    </div>
  );
}

function ComparisonRow({
  label,
  bch,
  btc,
  bchWins,
}: {
  label: string;
  bch: string;
  btc: string;
  bchWins: boolean;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${bchWins ? "text-primary" : "text-foreground"}`}>
        {bch}
        {bchWins && <Badge className="ml-1 text-[9px] bg-primary/15 text-primary">BCH</Badge>}
      </span>
      <span className="font-semibold tabular-nums text-muted-foreground w-16 text-right">{btc}</span>
    </div>
  );
}
